import { z } from "zod";
import { AppliedScenarioMetricKeySchema } from "./applied-scenario-metrics.js";
import { ConfidenceLevelSchema } from "./simulations-enums.js";

// Spec 079 (US1) — a RÉGUA DO GÊMEO: o que o modelo entregou, medido contra o que ele prometeu.
//
// Nada aqui é armazenado. Os quatro números saem de uma agregação sobre as tabelas que as
// iterações 1 a 3 da #1317 criaram (`applied_scenario`, `applied_scenario_metric`,
// `applied_scenario_classification`) — a acurácia vem da CLASSIFICAÇÃO vigente, nunca recomposta
// dos `withinTolerance` por métrica (FR-005), que respondem outra pergunta.
//
// ⚠️ TODO numérico é anulável, e `null` é AUSÊNCIA DE AMOSTRA — jamais `0` (FR-010). Um gêmeo sem
// histórico e um gêmeo que erra 100% das vezes são fatos opostos; colapsá-los no mesmo zero é a
// forma mais barata de fazer a tela mentir.

/** Qual das três réguas da cascata valeu (FR-014). */
export const TrackRecordSourceSchema = z.enum(["line_product", "line", "organization"]);
export type TrackRecordSourceKey = z.infer<typeof TrackRecordSourceSchema>;

/** Qual das duas bases produziu o nível exibido (FR-018). NUNCA um quinto valor do enum de nível. */
export const ConfidenceBasisSchema = z.enum(["provenance", "track_record"]);
export type ConfidenceBasisKey = z.infer<typeof ConfidenceBasisSchema>;

// ── A PONTE ENTRE O ENUM DO BANCO E A CHAVE DO CONTRATO ───────────────────────────────────────
//
// `ConfidenceBasis` e `TrackRecordSource` são enums Postgres em `UPPER_SNAKE` (convenção de todo
// enum do schema); a chave que atravessa o HTTP é minúscula, como toda chave de contrato. As duas
// grafias são de propósito, e a conversão mora AQUI — ao lado dos schemas Zod que as declaram —
// porque ela acontece nas DUAS pontas por caminhos que não se conhecem: o worker grava (congelando
// a execução) e a API lê (projetando a simulação).
//
// ⚠️ Mapas explícitos, e não `toUpperCase()`/`toLowerCase()`: um transformador de caixa aceitaria
// qualquer string e devolveria lixo tipado como enum válido no dia em que um valor novo entrasse
// só de um lado. Aqui, o valor novo não compila até ser declarado nos dois.

const BASIS_TO_DB = { provenance: "PROVENANCE", track_record: "TRACK_RECORD" } as const;
const SOURCE_TO_DB = {
  line_product: "LINE_PRODUCT",
  line: "LINE",
  organization: "ORGANIZATION",
} as const;

/** Enum do banco. Espelha `ConfidenceBasis`/`TrackRecordSource` do `schema.prisma`. */
export type ConfidenceBasisDb = (typeof BASIS_TO_DB)[ConfidenceBasisKey];
export type TrackRecordSourceDb = (typeof SOURCE_TO_DB)[TrackRecordSourceKey];

/** Chave do contrato → enum do banco. Nulo atravessa: "sem base declarada" é um estado legítimo. */
export function confidenceBasisToDb(key: ConfidenceBasisKey): ConfidenceBasisDb;
export function confidenceBasisToDb(key: ConfidenceBasisKey | null): ConfidenceBasisDb | null;
export function confidenceBasisToDb(key: ConfidenceBasisKey | null): ConfidenceBasisDb | null {
  return key === null ? null : BASIS_TO_DB[key];
}

export function trackRecordSourceToDb(key: TrackRecordSourceKey): TrackRecordSourceDb;
export function trackRecordSourceToDb(key: TrackRecordSourceKey | null): TrackRecordSourceDb | null;
export function trackRecordSourceToDb(
  key: TrackRecordSourceKey | null
): TrackRecordSourceDb | null {
  return key === null ? null : SOURCE_TO_DB[key];
}

/**
 * Enum do banco → chave do contrato.
 *
 * Tolera `null` e `undefined` porque é o que a leitura encontra: execução anterior à Spec 079 tem
 * as três colunas nulas, e isso significa "não havia régua quando isto rodou" — não é erro.
 */
export function confidenceBasisFromDb(db: string | null | undefined): ConfidenceBasisKey | null {
  return db === "PROVENANCE" ? "provenance" : db === "TRACK_RECORD" ? "track_record" : null;
}

export function trackRecordSourceFromDb(
  db: string | null | undefined
): TrackRecordSourceKey | null {
  return db === "LINE_PRODUCT"
    ? "line_product"
    : db === "LINE"
      ? "line"
      : db === "ORGANIZATION"
        ? "organization"
        : null;
}

/**
 * Mapa métrica → número, com todas as chaves de `METRIC_ORDER` presentes.
 *
 * Chaves fixas e não `z.record`: o conjunto de métricas é do domínio (`applied-scenario-metrics.ts`)
 * e a tela as percorre na ordem de lá. Um record aberto deixaria a resposta prometer menos do que a
 * tela assume, e a divergência apareceria como métrica faltando no dia em que a agregação mudasse.
 */
const MetricNumberMapSchema = z.record(
  AppliedScenarioMetricKeySchema,
  z.number().finite().nullable()
);

export const TrackRecordSummarySchema = z.object({
  /** Compromissos TESTADOS que sustentam a régua. Zero é zero — aqui não há ausência a declarar. */
  sampleSize: z.number().int().nonnegative(),
  /**
   * Percentual 0..100 — `WITHIN_TOLERANCE` sobre testados (FR-005).
   *
   * Percentual e não fração 0..1: todo indicador da plataforma é percentual (`oee-thresholds`,
   * `Decimal(5,2)`), e uma exceção de unidade é como nasce erro de duas ordens de grandeza.
   */
  accuracy: z.number().finite().nullable(),
  /** Percentual 0..100 — `proposedCause = confirmedCause` sobre as classificações vigentes (FR-008). */
  proposalAccuracy: z.number().finite().nullable(),
  /** `AVG(|realized − predicted|)` por métrica. Nunca somado entre métricas: unidades diferentes. */
  errorByMetric: MetricNumberMapSchema,
  /**
   * `AVG(realized − predicted)` por métrica — **com sinal**, e o sinal é a informação (FR-007).
   *
   * Negativo = o gêmeo prometeu MAIS do que a fábrica entregou. É o único número da tela cujo sinal
   * inverte o sentido da frase, e por isso ele nunca é exibido cru (ver `BiasValue.tsx`).
   */
  biasByMetric: MetricNumberMapSchema,
  /** `EXECUTION_NON_ADHERENCE` — a previsão não foi TESTADA, e não conta em lugar nenhum (FR-003). */
  untestedCount: z.number().int().nonnegative(),
  /** Apurado e ainda sem classificação vigente — trabalho pendente, não acerto nem erro (FR-003). */
  awaitingJudgmentCount: z.number().int().nonnegative(),
});
export type TrackRecordSummary = z.infer<typeof TrackRecordSummarySchema>;

export const TrackRecordProductSchema = TrackRecordSummarySchema.extend({
  productId: z.string().uuid(),
  productName: z.string(),
});
export type TrackRecordProduct = z.infer<typeof TrackRecordProductSchema>;

export const TrackRecordLineSchema = TrackRecordSummarySchema.extend({
  lineId: z.string().uuid(),
  lineName: z.string(),
  /**
   * O MARCO ZERO (FR-022): instante da última pendência de calibração `RESOLVED` da linha.
   *
   * `null` = a linha nunca teve pendência resolvida. Precisa aparecer na tela: sem ele, uma linha
   * cuja régua acabou de ser zerada é indistinguível de uma linha que nunca teve histórico —
   * e as duas pedem ações opostas.
   */
  zeroedAt: z.string().nullable(),
  products: z.array(TrackRecordProductSchema),
});
export type TrackRecordLine = z.infer<typeof TrackRecordLineSchema>;

export const TrackRecordOverviewSchema = z.object({
  organization: TrackRecordSummarySchema,
  lines: z.array(TrackRecordLineSchema),
});
export type TrackRecordOverview = z.infer<typeof TrackRecordOverviewSchema>;

export const TrackRecordEnvelopeSchema = z.object({
  data: TrackRecordOverviewSchema,
  error: z.null(),
});
export type TrackRecordEnvelope = z.infer<typeof TrackRecordEnvelopeSchema>;

// ── O NÍVEL EXIBIDO como TRÊS CAMPOS IRMÃOS (FR-015/FR-018) ───────────────────────────────────
//
// Repetido em quatro projeções (a simulação, o candidato do lote, o compromisso aplicado e o
// preview), e por isso declarado UMA vez. Os três andam JUNTOS de propósito: o nível sem a base é
// um número sem procedência, e a base sem a régua que a produziu não diz onde ir consertar.
//
// ⚠️ `.default(null)` em todos: projeção antiga (execução anterior a esta feature) não os traz, e o
// consumidor cai em `confidenceLevel` — o nível por PROCEDÊNCIA, que continua viajando ao lado e
// NÃO muda de sentido.
export const ConfidenceDisplayShape = {
  displayedConfidenceLevel: ConfidenceLevelSchema.nullable().default(null),
  confidenceBasis: ConfidenceBasisSchema.nullable().default(null),
  trackRecordSource: TrackRecordSourceSchema.nullable().default(null),
} as const;
